import { Icon } from "@uzverse/ui";
import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { UseVoice } from "./useVoice.js";
import "./voice.css";

export interface VoiceButtonProps {
  /** The page's voice hook (`useVoice()`), so the page can also `speak()` replies. */
  voice: UseVoice;
  /** Called with the recognized text after the button is released (never with empty text). */
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

/** Push-to-talk microphone button with listening/transcribing/speaking indicators. */
export function VoiceButton({ voice, onTranscript, disabled }: VoiceButtonProps) {
  const { t } = useTranslation("web");
  const { state } = voice;
  const holding = useRef(false);
  const unavailable = state.mode === "none";

  const begin = () => {
    if (holding.current || disabled || unavailable) return;
    holding.current = true;
    voice.listen().then(
      (text) => {
        if (text.trim()) onTranscript(text.trim());
      },
      () => undefined, // The error is shown from `state.error`.
    );
  };
  const end = () => {
    if (!holding.current) return;
    holding.current = false;
    voice.stopListening();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === " " || e.key === "Enter") && !e.repeat) {
      e.preventDefault();
      begin();
    }
  };
  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      end();
    }
  };

  const status = state.listening
    ? t("voice.listening")
    : state.transcribing
      ? t("voice.transcribing")
      : state.speaking
        ? t("voice.speaking")
        : null;
  const error = unavailable
    ? t("voice.unavailable")
    : state.error === "micDenied"
      ? t("voice.micDenied")
      : state.error === "failed"
        ? t("voice.failed")
        : null;

  const active = state.listening || state.transcribing || state.speaking;
  return (
    <div className="voice">
      {(status || error) && (
        <div className="voice-pop">
          {status && (
            <span
              role="status"
              className={`voice-status${state.listening ? " is-recording" : ""}${state.speaking ? " is-speaking" : ""}`}
            >
              {active && (
                <span className="wave" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
              )}
              {status}
            </span>
          )}
          {error && (
            <span role="alert" className="note note-error small">
              {error}
            </span>
          )}
        </div>
      )}
      {state.speaking && (
        <button
          type="button"
          className="btn btn-sm voice-stop"
          onClick={() => voice.stopSpeaking()}
        >
          <Icon name="stop" size={14} />
          {t("voice.stopSpeaking")}
        </button>
      )}
      <button
        type="button"
        className={`btn btn-icon voice-talk${state.listening ? " voice-active" : ""}`}
        aria-pressed={state.listening}
        aria-label={t("voice.talk")}
        title={state.mode === "browser" ? t("voice.browserMode") : t("voice.talk")}
        disabled={disabled || unavailable || state.transcribing}
        onPointerDown={begin}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      >
        <Icon name="mic" size={18} />
      </button>
    </div>
  );
}
